/**
 * ProjectLinkIcons — Feb 2026 R293n
 *
 * Compact row of service icons for a project's quick-links (WhatsApp
 * group, Google Drive, Dropbox, Figma, GitHub, Notion, Slack, YouTube,
 * generic links…). Hover shows the link label + URL, click opens the
 * destination in a new tab.
 *
 * Used in two places:
 *   • Project detail header (next to the status chip row).
 *   • Projects list column cell.
 *
 * The caller passes the `links` array (as returned by
 * `/projects/{pid}/links`). The component also knows how to detect
 * the service kind from a bare URL, so older rows without a `kind`
 * field still render the correct icon.
 */
import { useMemo } from "react";
import {
  MessageCircle,       // WhatsApp
  HardDrive,           // Drive / Dropbox fallback
  FileText,            // Google Docs
  Table2,              // Google Sheets
  Presentation,        // Google Slides
  CalendarDays,        // Google Calendar
  Video,               // Meet / Zoom
  Figma,               // Figma
  Github,              // GitHub / GitLab (reuses GitHub icon)
  StickyNote,          // Notion
  Hash,                // Slack
  Trello,              // Trello
  Database as GridDb,  // Airtable
  Youtube,             // YouTube
  Film,                // Vimeo
  Linkedin,            // LinkedIn
  Rocket,              // Jira (closest icon in lucide)
  Palette,             // Miro
  Link as LinkIcon,    // Generic
} from "lucide-react";

const KIND_META = {
  whatsapp:        { Icon: MessageCircle, cls: "text-[#25D366]",        name: "WhatsApp" },
  google_drive:    { Icon: HardDrive,     cls: "text-[#1A73E8]",        name: "Google Drive" },
  google_docs:     { Icon: FileText,      cls: "text-[#1A73E8]",        name: "Google Docs" },
  google_sheets:   { Icon: Table2,        cls: "text-[#0F9D58]",        name: "Google Sheets" },
  google_slides:   { Icon: Presentation,  cls: "text-[#F4B400]",        name: "Google Slides" },
  google_calendar: { Icon: CalendarDays,  cls: "text-[#4285F4]",        name: "Google Calendar" },
  google_meet:     { Icon: Video,         cls: "text-[#00897B]",        name: "Google Meet" },
  dropbox:         { Icon: HardDrive,     cls: "text-[#0061FF]",        name: "Dropbox" },
  figma:           { Icon: Figma,         cls: "text-[#A259FF]",        name: "Figma" },
  github:          { Icon: Github,        cls: "text-foreground",       name: "GitHub" },
  gitlab:          { Icon: Github,        cls: "text-[#E24329]",        name: "GitLab" },
  notion:          { Icon: StickyNote,    cls: "text-foreground",       name: "Notion" },
  slack:           { Icon: Hash,          cls: "text-[#4A154B]",        name: "Slack" },
  trello:          { Icon: Trello,        cls: "text-[#0079BF]",        name: "Trello" },
  airtable:        { Icon: GridDb,        cls: "text-[#FCB400]",        name: "Airtable" },
  youtube:         { Icon: Youtube,       cls: "text-[#FF0000]",        name: "YouTube" },
  vimeo:           { Icon: Film,          cls: "text-[#1AB7EA]",        name: "Vimeo" },
  zoom:            { Icon: Video,         cls: "text-[#2D8CFF]",        name: "Zoom" },
  linkedin:        { Icon: Linkedin,      cls: "text-[#0A66C2]",        name: "LinkedIn" },
  jira:            { Icon: Rocket,        cls: "text-[#0052CC]",        name: "Jira" },
  miro:            { Icon: Palette,       cls: "text-[#FFD02F]",        name: "Miro" },
  generic:         { Icon: LinkIcon,      cls: "text-muted-foreground", name: "Link" },
};

const HOST_KIND_MAP = {
  "chat.whatsapp.com":   "whatsapp",
  "wa.me":               "whatsapp",
  "whatsapp.com":        "whatsapp",
  "drive.google.com":    "google_drive",
  "docs.google.com":     "google_docs",
  "sheets.google.com":   "google_sheets",
  "slides.google.com":   "google_slides",
  "calendar.google.com": "google_calendar",
  "meet.google.com":     "google_meet",
  "dropbox.com":         "dropbox",
  "www.dropbox.com":     "dropbox",
  "figma.com":           "figma",
  "www.figma.com":       "figma",
  "github.com":          "github",
  "www.github.com":      "github",
  "gitlab.com":          "gitlab",
  "notion.so":           "notion",
  "www.notion.so":       "notion",
  "slack.com":           "slack",
  "app.slack.com":       "slack",
  "trello.com":          "trello",
  "airtable.com":        "airtable",
  "youtube.com":         "youtube",
  "www.youtube.com":     "youtube",
  "youtu.be":            "youtube",
  "vimeo.com":           "vimeo",
  "zoom.us":             "zoom",
  "linkedin.com":        "linkedin",
  "www.linkedin.com":    "linkedin",
  "miro.com":            "miro",
};

export function detectKind(url) {
  try {
    const host = new URL(url).hostname.toLowerCase();
    if (HOST_KIND_MAP[host]) return HOST_KIND_MAP[host];
    if (host.endsWith(".atlassian.net")) return "jira";
    if (host.endsWith(".slack.com")) return "slack";
    return "generic";
  } catch {
    return "generic";
  }
}

export function kindMeta(kind) {
  return KIND_META[kind] || KIND_META.generic;
}

/**
 * ProjectLinkIcons
 *
 * @param {object[]} links
 * @param {number} [max=4]  Show at most N icons inline; the rest go behind a +N chip.
 * @param {"xs"|"sm"|"md"} [size="sm"]
 */
export default function ProjectLinkIcons({ links = [], max = 4, size = "sm", testidBase = "project-link" }) {
  const sized = useMemo(() => {
    return (links || []).map((l) => {
      const kind = l.kind || detectKind(l.url);
      return { ...l, kind };
    });
  }, [links]);

  if (!sized.length) return null;

  const shown = sized.slice(0, max);
  const extra = sized.length - shown.length;
  const iconClass = size === "xs" ? "w-3 h-3" : size === "md" ? "w-4 h-4" : "w-3.5 h-3.5";
  const tileClass =
    size === "xs" ? "h-5 w-5"
      : size === "md" ? "h-7 w-7"
      : "h-6 w-6";

  return (
    <span className="inline-flex items-center gap-1" data-testid={`${testidBase}-row`}>
      {shown.map((link) => {
        const { Icon, cls, name } = kindMeta(link.kind);
        const title = `${link.label} · ${name}\n${link.url}`;
        return (
          <a
            key={link.id || link.url}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title={title}
            aria-label={`Open ${link.label} (${name}) in a new tab`}
            className={`inline-flex items-center justify-center ${tileClass} border border-border bg-background hover:bg-muted transition-colors`}
            data-testid={`${testidBase}-icon-${link.id || link.kind}`}
          >
            <Icon className={`${iconClass} ${cls}`} />
          </a>
        );
      })}
      {extra > 0 && (
        <span
          className={`inline-flex items-center justify-center ${tileClass} border border-border bg-muted text-[10px] font-semibold text-muted-foreground`}
          title={sized.slice(max).map((l) => `${l.label} · ${l.url}`).join("\n")}
          data-testid={`${testidBase}-overflow`}
        >
          +{extra}
        </span>
      )}
    </span>
  );
}
